import React, { useCallback } from 'react';
import { MultiSelectDropdown, debounce, useToasts } from 'freemium-ui';
import { get } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MIN_TAG_LENGTH } from 'routes/screens/response/constants';
import {
  useAddTagMutation,
  useGetTagInfo,
  useGetTags,
} from 'routes/screens/response/hooks';
import { isTagValid } from 'routes/utils';
import { WorkflowType } from 'shared/types';
import { ActionContainer } from '../../styles';

type AddTagsProps = {
  disabled: boolean;
  actionIndex: number;
};

const AddTags = (props: AddTagsProps) => {
  const { disabled, actionIndex } = props;
  const { t } = useTranslation();
  const {
    watch,
    setValue,
    formState: { errors, submitCount },
  } = useFormContext<WorkflowType>();
  const [selectedItems, setSelectedItems] = React.useState<any[]>([]);
  const [tagName, setTagName] = React.useState('');
  const [, setToast] = useToasts();

  const tags = watch(`actions.${actionIndex}.action_meta.tags`);

  const { data: tagsData = [] } = useGetTagInfo(tags?.length ? tags : [], '');

  React.useEffect(() => {
    if (tagsData?.length > 0) {
      setSelectedItems(tagsData);
    }
  }, [tagsData]);

  const {
    data: tagsSearchData = [],
    refetch: refetchTags,
    isLoading: isTagsLoading,
    isFetching: isTagsFetching,
  } = useGetTags({ searchParam: tagName });

  const handleInputChange = (inputVal: string) => {
    if (!disabled && inputVal.length) {
      debouncedSearch();
      setTagName(inputVal);
    }
  };

  type TagType = {
    id?: any;
    name: string;
    deleted?: boolean;
  };

  const { mutate: addTag } = useAddTagMutation({
    callback: (isSuccess: boolean, response: any) => {
      if (isSuccess) {
        setToast({
          delay: 3000,
          type: 'success',
          text: `${t('forms.toast.success.tag_created')}`,
        });
        selectedItems.push(response);
        setSelectedItems(selectedItems);
        setValue(
          `actions.${actionIndex}.action_meta.tags`,
          selectedItems.map((tag) => tag.id),
        );
      } else {
        setToast({
          delay: 3000,
          type: 'error',
          text: `${t('forms.toast.error.tag_creation_failed')}`,
        });
      }
    },
  });

  const createTagIfNotExists = (payload: { type: string; value: any[] }) => {
    const items = payload?.value;
    // when removing tags, items will be undefined when removing last item. So adding if check
    if (items) {
      refetchTags();
      const tagName: string =
        items && items.length > 0 ? items[items.length - 1].name : null;
      if (tagName && tagName.length > 2 && isTagValid(tagName)) {
        // the OR condition is while removing the selected tags, it will create tag. SO to prevent it
        const isTagExists: boolean =
          tagsSearchData?.accountTags.some(
            (item: TagType) => item.name === tagName,
          ) || selectedItems.some((item) => item.name === tagName);
        if (!isTagExists) {
          if (tagName.length <= MIN_TAG_LENGTH) {
            setToast({
              delay: 3000,
              type: 'error',
              text: `${t('forms.err_msg.invalid_tag_name')}`,
            });
            return;
          } else {
            addTag({
              payload: {
                name: tagName,
              },
              responseId: '',
            });
          }
        }
      }
    }
  };

  const debouncedSearch = useCallback(
    debounce(() => {
      refetchTags();
    }, 500),
    [],
  );

  const updateValues = (items: any[]) => {
    if (!disabled) {
      const payload = {
        type: 'MULTISELECT',
        value: items,
      };
      setSelectedItems(items);
      createTagIfNotExists(payload);
      setValue(
        `actions.${actionIndex}.action_meta.tags`,
        items.map((tag) => tag.id),
      );
    }
  };

  return (
    <ActionContainer>
      <MultiSelectDropdown
        name={t('forms.placeholders.add_tags')}
        items={tagsSearchData?.accountTags || []}
        selectedItem={selectedItems || []}
        onChange={updateValues}
        isMenuLoading={isTagsFetching || isTagsLoading}
        customValues
        disabled={disabled}
        itemLabelIdentifier="name"
        itemValueIdentifier="name"
        label={`${t('forms.placeholders.add_tags')}`}
        placeholder={
          selectedItems?.length ? '' : `${t('forms.placeholders.search_char')}`
        }
        handleInputChange={handleInputChange}
        isDynamicMenu
        isErrored={
          submitCount > 0 &&
          !!get(errors, `actions.${actionIndex}.action_meta.tags.message`)
        }
        errorText={get(
          errors,
          `actions.${actionIndex}.action_meta.tags.message`,
          '',
        )}
      />
    </ActionContainer>
  );
};

export default AddTags;
